import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';

const badgeVariants = cva(
  'inline-flex max-w-full items-center justify-center rounded-full border font-semibold align-middle',
  {
    variants: {
      align: {
        center: 'justify-center text-center',
        start: 'justify-start text-left',
      },
      appearance: {
        soft: 'border-transparent bg-secondary text-secondary-foreground',
        solid: 'border-transparent bg-foreground text-background',
        outline: 'border-border bg-transparent text-foreground',
      },
      size: {
        sm: 'min-h-6 gap-1 px-2 py-0.5 text-xs leading-4',
        md: 'min-h-7 gap-1.5 px-2.5 py-1 text-xs leading-4',
      },
      wrap: {
        nowrap: 'whitespace-nowrap',
        wrap: 'whitespace-normal',
      },
    },
    defaultVariants: {
      align: 'center',
      appearance: 'soft',
      size: 'sm',
      wrap: 'nowrap',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

function Badge({ align, appearance, children, className, icon, size, wrap, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ align, appearance, size, wrap }), className)} {...props}>
      {icon ? (
        <span aria-hidden="true" className="shrink-0 leading-none">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
